



import React, { useEffect, useRef, useState } from 'react';
import ImageSkeleton from './ImageSkeleton';
import { preloadAuthorizedImage } from '../utils/authorizedImageCache';

function DepartmentCardImage({
  src,
  alt = '',
  className = 'h-full w-full object-cover',
  priority = false,
}) {
  const imageRef = useRef(null);
  const [displaySrc, setDisplaySrc] = useState(src);
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  const [useAuthFallback, setUseAuthFallback] = useState(false);

  useEffect(() => {
    setDisplaySrc(src);
    setLoaded(false);
    setFailed(false);
    setUseAuthFallback(false);
  }, [src]);

  // Cached images can already be complete when remounting after navigation.
  useEffect(() => {
    const img = imageRef.current;
    if (!img || !displaySrc) return;
    if (!img.complete) return;
    if (img.naturalWidth > 0) {
      setLoaded(true);
      return;
    }
    if (!useAuthFallback) {
      setUseAuthFallback(true);
    } else {
      setFailed(true);
      setLoaded(true);
    }
  }, [displaySrc, useAuthFallback]);

  useEffect(() => {
    if (!useAuthFallback || !src) return undefined;

    let cancelled = false;
    preloadAuthorizedImage(src).then((url) => {
      if (!cancelled && url) {
        setDisplaySrc((prev) => {
          if (url !== prev) setLoaded(false);
          return url === prev ? prev : url;
        });
      }
    });

    return () => {
      cancelled = true;
    };
  }, [useAuthFallback, src]);

  if (!displaySrc) {
    return <ImageSkeleton className="h-full w-full" />;
  }

  return (
    <div className="relative h-full w-full">
      {!loaded ? <ImageSkeleton className="absolute inset-0" /> : null}
      {failed ? (
        <div className="h-full w-full bg-gradient-to-br from-gray-200 to-gray-100" />
      ) : (
        <img
          ref={imageRef}
          src={displaySrc}
          alt={alt}
          className={`${className} transition-opacity duration-300 ${loaded ? 'opacity-100' : 'opacity-0'}`}
          loading={priority ? 'eager' : 'lazy'}
          decoding="async"
          fetchPriority={priority ? 'high' : 'auto'}
          onLoad={() => setLoaded(true)}
          onError={() => {
            if (!useAuthFallback) {
              setUseAuthFallback(true);
              return;
            }
            setFailed(true);
            setLoaded(true);
          }}
        />
      )}
    </div>
  );
}

export default DepartmentCardImage;
